import { useEffect, useState, useCallback } from 'react'
import { resenasApi } from '../../api/resenas'
import { fmtFechaHora, toISOLocal } from '../../utils/fecha'
import { colors, font, text } from '../../design/tokens'
import { Cargando, ErrorMsg, Vacio, Titulo, TabsSegmento, Seccion } from './comunes'

// Reseñas en el celular — la MISMA lectura que el escritorio (Clientes →
// Reseñas), en formato de bolsillo: puntaje general del período, medidor por
// pregunta y las voces una por una con su nota y su banda de color.
const TIPOS = [
  { k: 'entrega', label: 'Entrega' },
  { k: 'compra', label: 'Compra' },
  { k: 'evento', label: 'Eventos' },
]
const RANGOS = [
  { k: 30, label: '30 días' },
  { k: 90, label: '90 días' },
  { k: 365, label: 'Año' },
]

// Una sola escala de color, la misma del escritorio (Encuesta::banda()):
// 4-5 buena · 3 regular · 1-2 mala (en 0-10: ≥7 · 5-6 · ≤4).
const COLOR_BANDA = { buena: '#16a34a', regular: '#d97706', mala: colors.err }
const colorValor = (v, de = 5) => {
  const x = de === 10 ? v / 2 : v
  return x >= 4 ? COLOR_BANDA.buena : x >= 3 ? COLOR_BANDA.regular : COLOR_BANDA.mala
}

const desdeDias = (dias) => {
  const d = new Date()
  d.setDate(d.getDate() - dias)
  return toISOLocal(d)
}

export default function MovilResenas() {
  const [tipo, setTipo] = useState('entrega')
  const [dias, setDias] = useState(30)
  const [datos, setDatos] = useState(null)
  const [error, setError] = useState(null)

  const cargar = useCallback(async () => {
    setDatos(null)
    try {
      const { data } = await resenasApi.lectura({ tipo, desde: desdeDias(dias) })
      setDatos(data)
      setError(null)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudieron cargar las reseñas')
    }
  }, [tipo, dias])

  useEffect(() => { cargar() }, [cargar])

  const voces = datos?.voces || []

  return (
    <div>
      <Titulo>Reseñas</Titulo>
      <TabsSegmento options={TIPOS} value={tipo} onChange={setTipo} />
      <TabsSegmento options={RANGOS} value={dias} onChange={setDias} />
      {error && <ErrorMsg>{error}</ErrorMsg>}
      {!datos && !error && <Cargando />}

      {datos && (
        <>
          <ResumenGeneral d={datos} />
          <Preguntas preguntas={datos.preguntas || []} />

          <Seccion titulo={`Reseña por reseña (${voces.length})`}>
            {voces.length === 0 && <Vacio>Sin reseñas en el período</Vacio>}
            {voces.map((v) => <Voz key={v.id} v={v} tipo={tipo} />)}
          </Seccion>
          <p style={{ marginTop: 12, fontSize: 12, color: colors.textMeta, fontFamily: font.sans }}>
            Atender una reseña se hace desde LGR completo (Clientes → Reseñas).
          </p>
        </>
      )}
    </div>
  )
}

// ── El número grande del período: entrega = promedio de 5, compra = NPS ─────
function ResumenGeneral({ d }) {
  const g = d.general   // entrega
  const i = d.indice    // compra (NPS)

  if (g) {
    return (
      <div style={resumen}>
        <span style={{ ...granNum, color: colorValor(g.promedio) }}>{g.promedio}</span>
        <span style={resumenTxt}>
          <b>La entrega</b>, de 5 · {g.contestaron} puntuaron · {d.respondidas} respuestas
        </span>
      </div>
    )
  }
  if (i) {
    return (
      <div style={{ ...resumen, flexWrap: 'wrap' }}>
        <span style={{ ...granNum, color: i.nps >= 50 ? COLOR_BANDA.buena : i.nps >= 0 ? COLOR_BANDA.regular : COLOR_BANDA.mala }}>{i.nps}</span>
        <span style={resumenTxt}>
          <b>NPS de compra</b> · {i.contestaron} puntuaron<br />
          <span style={{ color: COLOR_BANDA.buena }}>▲ {i.promotores}</span>
          {' · '}<span style={{ color: colors.textSec }}>― {i.pasivos}</span>
          {' · '}<span style={{ color: COLOR_BANDA.mala }}>▼ {i.detractores}</span>
        </span>
      </div>
    )
  }
  return (
    <div style={resumen}>
      <span style={resumenTxt}>{d.respondidas} respuesta{d.respondidas === 1 ? '' : 's'} en el período</span>
    </div>
  )
}

// ── Medidores por pregunta (promedio + reparto), como en el escritorio ──────
function Preguntas({ preguntas }) {
  if (!preguntas.length) return null
  return (
    <Seccion titulo="Por pregunta">
      {preguntas.map((p) => <Pregunta key={p.clave} p={p} />)}
    </Seccion>
  )
}

function Pregunta({ p }) {
  const maxN = Math.max(...(p.reparto || []).map((r) => r.n), 1)
  return (
    <div style={{ padding: '8px 0', borderBottom: `1px solid ${colors.borderSoft}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 13.5, color: colors.text, flex: 1 }}>{p.label}</span>
        {p.promedio != null && (
          <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 15, color: colorValor(p.promedio, p.de || 5) }}>
            {p.promedio}
          </span>
        )}
      </div>

      {p.forma === 'escala' && (p.reparto || []).length > 0 && (
        <div style={{ marginTop: 5 }}>
          {p.reparto.map((r) => (
            <div key={r.valor} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '1px 0' }}>
              <span style={repNum}>{r.valor}</span>
              <div style={{ flex: 1, height: 5, background: colors.borderSoft, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${(r.n / maxN) * 100}%`, height: '100%', background: colorValor(r.valor), borderRadius: 3 }} />
              </div>
              <span style={{ ...repNum, width: 22, textAlign: 'right' }}>{r.n || ''}</span>
            </div>
          ))}
        </div>
      )}

      {p.forma === 'si_no' && (
        <div style={{ marginTop: 3, fontSize: 12.5, fontFamily: font.sans, color: colors.textSec }}>
          <span style={{ color: COLOR_BANDA.buena, fontWeight: 600 }}>sí {p.si}</span>
          {' · '}<span style={{ color: p.no > 0 ? COLOR_BANDA.mala : colors.textSec, fontWeight: 600 }}>no {p.no}</span>
          {p.sin_contestar > 0 && <span style={{ color: colors.textMeta }}> · {p.sin_contestar} sin contestar</span>}
        </div>
      )}

      {p.forma === 'opciones' && (p.opciones || []).length > 0 && (
        <div style={{ marginTop: 3, fontSize: 12.5, fontFamily: font.sans, color: colors.textSec }}>
          {p.opciones.map((o) => `${o.label} ${o.n}`).join(' · ')}
        </div>
      )}
    </div>
  )
}

// ── Cada reseña, con su nota y su banda de color ────────────────────────────
function Voz({ v, tipo }) {
  const color = COLOR_BANDA[v.banda] || colors.textSec
  // Compra SIEMPRE en la escala 0-10 del índice (opción A, 29 ago): las nuevas
  // guardan nps = nps5×2, así conviven con las viejas sin mezclar escalas.
  const [punt, de] = tipo === 'compra' && v.nps != null ? [v.nps, 10] : [v.puntaje, v.puntaje_de]
  return (
    <div style={{ padding: '9px 0', borderBottom: `1px solid ${colors.borderSoft}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 14, color: colors.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {v.cliente || 'Reseña'}
        </span>
        {punt != null && (
          <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 14.5, color, flexShrink: 0 }}>
            {punt}/{de}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: colors.textMeta, fontFamily: font.sans, marginTop: 2 }}>
        {v.respondida_at ? fmtFechaHora(v.respondida_at) : ''}
        {v.quien && tipo === 'entrega' ? ` · entregó ${v.quien}` : ''}
        {v.para_atender ? <span style={{ color: COLOR_BANDA.mala, fontWeight: 600 }}> · ⚠ para atender</span> : ''}
        {v.atendida ? ` · ✔ atendida por ${v.atendida.por}` : ''}
      </div>
      {v.comentario && (
        <p style={{ margin: '5px 0 0', fontSize: 13.5, lineHeight: 1.5, color: colors.text, fontFamily: font.sans }}>
          “{v.comentario}”
        </p>
      )}
    </div>
  )
}

const resumen = {
  display: 'flex', alignItems: 'center', gap: 12, margin: '2px 0 10px',
  padding: '12px 14px', border: `1px solid ${colors.border}`, borderRadius: 12, background: colors.bg,
}
const granNum = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 30, fontWeight: 700, lineHeight: 1 }
const resumenTxt = { fontSize: 12.5, color: colors.textSec, fontFamily: font.sans, lineHeight: 1.5 }
const repNum = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 11, color: colors.textMeta, width: 12, flexShrink: 0 }
